/// <amd-module name="Wit/Settings/BoardsExternalIntegration/components/panels/addRepo/RepositorySelection" />
import { CheckboxVisibility, IColumn } from "OfficeFabric/DetailsList";
import { ProgressIndicator } from "OfficeFabric/ProgressIndicator";
import { IObjectWithKey, Selection, SelectionMode } from "OfficeFabric/Selection";
import * as React from "react";
import { ITfsPageService } from "Tfs/Platform/Page";
import * as StringUtil from "VSS/Core/Util/String";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { FilterBar } from "VSSUI/FilterBar";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { IPickListItem, PickListFilterBarItem } from "VSSUI/PickList";
import { Pill, PillSize } from "VSSUI/Pill";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { KeywordFilterBarItem } from "VSSUI/TextFilterBarItem";
import { css } from "VSSUI/Util";
import { Filter, FILTER_CHANGE_EVENT, IFilterState } from "VSSUI/Utilities/Filter";
import { VssDetailsList, VssDetailsListRowStyle, VssDetailsListTitleCell } from "VSSUI/VssDetailsList";
import { IVssIconProps, VssIconType } from "VSSUI/VssIcon";
import { ExternalGitRepo } from "Wit/Clients/Internal/Contracts/ExternalEvents";
import {
    GitHubConfigurationSettingsResponse,
    GitHubRepositoryListDataProviderRequest,
    GitHubRepositoryResponse,
    RepositoryListData,
    ResultSet
} from "Wit/Clients/Internal/Contracts/WorkItemTracking";
import { GitHubConnectionConstants } from "Wit/Settings/BoardsExternalIntegration/constants/Constants";
import * as Resources from "Wit/Settings/BoardsExternalIntegration/Resources";
import {
    GitHubIntegrationCIActions,
    publishGitHubIntegrationTelemetryWithPageContext
} from "Wit/Settings/BoardsExternalIntegration/telemetry/GitHub";

type FilterResultSet = ResultSet | string;

export interface IRepositorySelectionProps {
    serviceName: string;
    connectionId?: string;
    sourceProvider: string;
    onSelectedRepositoriesChanged: (repositories: ExternalGitRepo[], filterText?: string, selection?: Selection) => void;
    additionalRepositoryFilter: (repository: ExternalGitRepo) => boolean;
    onRenderRepositoryListHeader: () => JSX.Element | null;
    onRepositoryListUpdate?: (action: string, repoCount: number, filterText?: string, selection?: Selection) => void;
    emptyRepositoryInfoText?: string;
    strongBoxKey?: string;
    emptyRepositorySeverityLevel?: MessageCardSeverity;
    onError: (error: Error) => void;
    shouldPreselectRepo?: boolean;
    orgOrUserName?: string;
    isUserType?: boolean;
}

export interface IRepositorySelectionState {
    projectId: string;
    projectName: string;
    countRequests: number;
    filterText: string | undefined;
    filterNoReposFound: boolean;
    filterTypes: FilterItem[];
    repositories: ExternalGitRepo[] | undefined;
    repositoryList: RepositoryListData | undefined;
    resultSet: FilterResultSet;
    listKey?: string;
    showLoading?: boolean;
}

export interface FilterItem {
    key: number | string;
    displayName: string;
}

export namespace ColumnKeys {
    export const title = "title";
    export const lastUpdated = "lastUpdated";
}

export class RepositorySelection extends VssComponent<IRepositorySelectionProps, IRepositorySelectionState> {
    private _filterBarRef = React.createRef<FilterBar>();
    private _columns: IColumn[];
    private _mounted: boolean;
    private _selection: Selection;
    private _filter: Filter;
    private _contributionService: IVssContributionService;
    private _repoSettings: GitHubConfigurationSettingsResponse | undefined;

    constructor(props: IRepositorySelectionProps, context: IVssComponentContext) {
        super(props, context);

        const pageService = this.context.pageContext.getService<ITfsPageService>("ITfsPageService");
        const pageData = pageService.getData();

        this.state = {
            projectId: pageData!.project!.id,
            projectName: pageData!.project!.name,
            countRequests: 0,
            filterText: undefined,
            filterNoReposFound: false,
            repositories: undefined,
            repositoryList: undefined,
            resultSet: ResultSet.Top,
            listKey: "repository-list"
        } as IRepositorySelectionState;

        this._columns = this.getColumns();
        this._selection = new Selection({
            onSelectionChanged: this._onSelectedRepositoriesChanged,
            selectionMode: SelectionMode.multiple
        });

        this._contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");

        this._repoSettings = this._contributionService.getData<GitHubConfigurationSettingsResponse>(
            GitHubConnectionConstants.GitHubConfigurationSettingsDataProviderId
        );

        const initialSelection: FilterItem[] = [];
        this._filter = new Filter({
            defaultState: {
                filterType: {
                    value: !!initialSelection ? [initialSelection] : undefined
                }
            }
        });
        this._filter.subscribe(this._onFilterChanged, FILTER_CHANGE_EVENT);
    }

    public render(): JSX.Element {
        const showMessageBar =
            this.props.emptyRepositoryInfoText &&
            this.state &&
            this.state.repositories &&
            this.state.repositoryList &&
            !this.state.repositoryList.continuationToken &&
            this.state.repositories.length === 0;

        return (
            <div className={css(this.getClassName("repository-selection"))}>
                {
                    <FilterBar filter={this._filter} ref={this._filterBarRef}>
                        <KeywordFilterBarItem
                            filterItemKey={"filterText"}
                            placeholder={Resources.RepositorySelectorFilterPlaceholder}
                            throttleWait={400}
                        />
                        {this._showFilterDropdown() && (
                            <PickListFilterBarItem
                                filterItemKey={"filterType"}
                                getListItem={this._getFilterItem}
                                getPickListItems={this._getFilterItems}
                                selectionMode={SelectionMode.single}
                            />
                        )}
                    </FilterBar>
                }
                {showMessageBar && (
                    <MessageCard severity={this.props.emptyRepositorySeverityLevel || MessageCardSeverity.Info}>
                        {this.props.emptyRepositoryInfoText}
                    </MessageCard>
                )}
                {(this.state && !this.state.repositoryList) || // initial state
                    (this.state && this.state.repositoryList && this.state.repositoryList.continuationToken) ? (
                        <div>
                            <ProgressIndicator
                                className="progress"
                                progressHidden={!this.state.repositoryList}
                                percentComplete={this._getPercentageOfRepositoriesLoaded()}
                            />
                            <div className="subheader">{this.getDescriptionMessage()}</div>
                        </div>
                    ) : !showMessageBar ? (
                        <>
                            {this.props.onRenderRepositoryListHeader && this.props.onRenderRepositoryListHeader()}
                            <VssDetailsList
                                className="repository-list"
                                setKey={this.state.listKey}
                                isHeaderVisible={false}
                                items={this._getItems()}
                                hideGroupExpansion={true}
                                columns={this._columns}
                                selection={this._selection}
                                selectionPreservedOnEmptyClick={true}
                                checkboxVisibility={CheckboxVisibility.always}
                                rowStyle={VssDetailsListRowStyle.oneLine}
                            />
                        </>
                    ) : null}
                {this.state.showLoading && (
                    <div className="flex flex-grow justify-center flex-center repo-spinner">
                        <Spinner size={SpinnerSize.large} label={Resources.SpinnerFetchingRepositoriesText} />
                    </div>
                )}
            </div>
        );
    }

    public componentDidMount(): void {
        this._mounted = true;
        this._fetchRepositories();
        requestAnimationFrame(() => {
            if (this._filterBarRef.current) {
                this._filterBarRef.current.focus();
            }
        });
    }

    public componentWillUnmount(): void {
        this._mounted = false;
    }

    protected getColumns(): IColumn[] {
        const columns: IColumn[] = [
            {
                key: ColumnKeys.title,
                name: "",
                fieldName: "",
                minWidth: 100,
                className: "ci-repositories-list-main-column",
                onRender: (repository: ExternalGitRepo) => {
                    return <RepositoryItem repository={repository} iconProps={this.getIconProps(repository)} />;
                }
            }
        ];
        return columns;
    }

    protected getDescriptionMessage = (): string => {
        if (this.state && this.state.repositoryList && this.state.repositoryList.continuationToken) {
            const approxTotalRepos = this.state.repositoryList.repositories.length * this.state.repositoryList.totalPageCount;
            return StringUtil.format(Resources.FetchingRepositories, approxTotalRepos && approxTotalRepos.toLocaleString(), this.props.serviceName);
        }
        return "";
    };

    protected getIconProps(repository: ExternalGitRepo): IVssIconProps {
        const iconProps: IVssIconProps = { className: "repository-avatar-icon" };
        const { additionalProperties } = repository;
        const defaultImageAltText = Resources.GitHub; // If repo data not available, person/org icon will have alt text default to GitHub.

        if (additionalProperties && additionalProperties.ownerAvatarUrl) {
            iconProps.imageProps = {
                src: repository.additionalProperties.ownerAvatarUrl,
                alt: additionalProperties.repoNameWithOwner && typeof (additionalProperties.repoNameWithOwner) === "string"
                    ? additionalProperties.repoNameWithOwner.split("/")[0] : defaultImageAltText
            };
            iconProps.iconType = VssIconType.image;
        } else {
            iconProps.imageProps = {
                alt: defaultImageAltText
            };
            iconProps.iconName = "GitHubLogo";
            iconProps.iconType = VssIconType.fabric;
        }

        return iconProps;
    }

    private _preSelectItems(): void {
        if (
            this.props.shouldPreselectRepo &&
            this._repoSettings &&
            this.state.repositories &&
            this.state.repositories.length <= this._repoSettings.maxRepoCountForPreSelection
        ) {
            this._selection.setItems(this._getItems() as IObjectWithKey[]);
            this._selection.selectToIndex(this.state.repositories.length - 1);
            publishGitHubIntegrationTelemetryWithPageContext(this.context.pageContext, GitHubIntegrationCIActions.FRE_PRESELECT_REPO, {
                repositoryCount: this.state.repositories.length
            });
        }
    }

    private _showFilterDropdown(): boolean {
        return this.state.filterTypes && this.state.filterTypes.length > 0;
    }

    private _updateFilterDropdown(resultSet: FilterResultSet): boolean {
        return resultSet !== undefined && !this.isCustomResultSetFilter(resultSet);
    }

    private _fetchRepositories(): void {
        this.setState({
            countRequests: 0,
            repositories: undefined,
            resultSet: this.state.resultSet
        });

        const currentResultSet = this.state.resultSet;
        this.listRepositories(this.props.connectionId!, currentResultSet, true).then(
            x => this._onRepositoryListReceived(x, currentResultSet),
            this.props.onError
        );
    }

    private isCustomResultSetFilter(resultSet: FilterResultSet): boolean {
        return typeof resultSet === "string";
    }

    private _onFilterChanged = (newValue: IFilterState, action: string) => {
        if (newValue.hasOwnProperty("filterText")) {
            const filterText = (newValue.filterText && newValue.filterText.value) || "";
            const filterNoReposFound: boolean = this._filterNoReposFound(this.state.repositories || [], filterText);
            this.setState(
                {
                    filterText: filterText,
                    filterNoReposFound: filterNoReposFound,
                    listKey: StringUtil.newGuid()
                },
                () =>
                    this.props.onRepositoryListUpdate &&
                    this.props.onRepositoryListUpdate("ReposFilter", this._getItems().length, filterText, this._selection)
            );
        }
        if (newValue.hasOwnProperty("filterType") && newValue.filterType && newValue.filterType.value) {
            const filterType = newValue.filterType && newValue.filterType.value && newValue.filterType.value[0];
            // For GitHub App, we always show Top repositories so we don't render filter drop down.
            if (filterType && filterType.key !== undefined && filterType.key !== this.state.resultSet) {
                this.setState(
                    {
                        resultSet: filterType.key
                    },
                    () => {
                        this._fetchRepositories();
                    }
                );
            }
        }
    };

    private _getFilterItems = () => {
        return this.state.filterTypes;
    };

    private _getFilterItem = (item: FilterItem): IPickListItem => {
        return {
            name: item.displayName,
            key: item.key
        } as IPickListItem;
    };

    private _onRepositoryListReceived = (sourceRepositories: RepositoryListData, resultSet: FilterResultSet) => {
        if (this._mounted && resultSet === this.state.resultSet && sourceRepositories) {
            const state: IRepositorySelectionState = {} as IRepositorySelectionState;
            let repositories = (this.state.repositories || []).concat(sourceRepositories.repositories);
            let callback = undefined;
            if (sourceRepositories.continuationToken) {
                this.listRepositories(this.props.connectionId, this.state.resultSet, true /*pageResults*/, sourceRepositories.continuationToken).then(
                    x => this._onRepositoryListReceived(x, resultSet),
                    (this.props as IRepositorySelectionProps).onError
                );
            } else {
                if (this._updateFilterDropdown(resultSet)) {
                    state.filterTypes = sourceRepositories.resultSets || [];
                    callback = () => {
                        const currentResultsKey = sourceRepositories.currentResults && sourceRepositories.currentResults.key;
                        const currentFilter = state.filterTypes.find(x => x.key === currentResultsKey) || this.state.resultSet;
                        const currentFilterObj = { value: currentFilter && [currentFilter] };
                        this._filter.setDefaultState({ filterType: currentFilterObj });
                        this._filter.setFilterItemState("filterType", currentFilterObj);

                        // Pre-select repo
                        this._preSelectItems();
                    };
                }

                // additional filtering the consuming component may request,
                // example is only wanting to show repos you have admin permissions on
                if (this.props.additionalRepositoryFilter) {
                    repositories = repositories.filter(this.props.additionalRepositoryFilter);
                }

                // Sort by Last Update if that column is available
                if (this._columns.find(column => column.key === ColumnKeys.lastUpdated)) {
                    repositories = repositories.sort((a, b) => {
                        const timeA: number = Date.parse(a.additionalProperties.lastUpdated);
                        const timeB: number = Date.parse(b.additionalProperties.lastUpdated);
                        const delta: number = timeB - timeA; // sort newest first
                        return isNaN(delta) ? 0 : delta;
                    });
                }
                // Otherwise, sort by full name (case insensitive)
                else {
                    repositories = repositories.sort((a, b) => {
                        return StringUtil.localeIgnoreCaseComparer(
                            a.additionalProperties.repoNameWithOwner || a.name,
                            b.additionalProperties.repoNameWithOwner || b.name
                        );
                    });
                }

                const filterNoReposFound: boolean = this._filterNoReposFound(repositories, this.state.filterText || "");
                state.filterNoReposFound = filterNoReposFound;

                // only calling list updated once we are done paging and have the total results
                this.props.onRepositoryListUpdate &&
                    this.props.onRepositoryListUpdate(
                        "Repos" + (resultSet && resultSet.toString()),
                        repositories.length,
                        this.state.filterText,
                        this._selection
                    );
            }

            state.repositoryList = sourceRepositories;
            state.repositories = repositories;
            state.countRequests = this.state.countRequests + 1;
            state.showLoading = false;
            this.setState(state, callback);
        }
    };

    private _onSelectedRepositoriesChanged = (): void => {
        const selectedRepositories: ExternalGitRepo[] = this._selection.getSelection() as ExternalGitRepo[];
        this.props.onSelectedRepositoriesChanged &&
            this.props.onSelectedRepositoriesChanged(selectedRepositories, this.state.filterText, this._selection);
    };

    private _getPercentageOfRepositoriesLoaded = (): number | undefined => {
        if (this.state && this.state.repositoryList) {
            return this.state.countRequests / this.state.repositoryList.totalPageCount;
        }
        return undefined;
    };

    private _getItems = (): ExternalGitRepo[] => {
        if (this.state && this.state.repositories) {
            return this.state.filterText ? this._filterRepositories(this.state.repositories, this.state.filterText) : this.state.repositories;
        }
        return [];
    };

    private _filterRepositories(repositories: ExternalGitRepo[], filterText: string): ExternalGitRepo[] {
        if (repositories) {
            const searchForString = filterText.trim().toLowerCase();
            return repositories.filter(a => a.additionalProperties.repoNameWithOwner.toLowerCase().indexOf(searchForString) !== -1);
        }
        return repositories;
    }

    private _filterNoReposFound(repositories: ExternalGitRepo[], newValue: string): boolean {
        return this._filterRepositories(repositories, newValue).length === 0;
    }

    private async listRepositories(
        serviceEndpointId?: string,
        resultSet?: FilterResultSet,
        pageResults?: boolean,
        continuationToken?: string
    ): Promise<RepositoryListData> {
        const repositoryResponse = this._contributionService.getData<GitHubRepositoryResponse>(
            GitHubConnectionConstants.GitHubPreCreateDataProviderId
        );

        // If its a preselect scenario then we already have repository scoped to user & app. So using instead of fetching again.
        if (repositoryResponse && repositoryResponse.repositoryData) {
            return repositoryResponse.repositoryData;
        } else {
            this.setState({ showLoading: true });
            const parameters: GitHubRepositoryListDataProviderRequest = {
                resultSet: resultSet!,
                pageResults: pageResults!,
                continuationToken: continuationToken!,
                sourceProvider: this.props.sourceProvider,
                isUserType: this.props.isUserType,
                orgOrUserName: this.props.orgOrUserName
            } as GitHubRepositoryListDataProviderRequest;

            if (serviceEndpointId) {
                parameters.serviceEndpointId = serviceEndpointId;
            } else {
                parameters.strongBoxKey = this.props.strongBoxKey!;
            }

            const repositories = await this._contributionService.getDataAsync<RepositoryListData>(
                GitHubConnectionConstants.RepositoryDataProviderId,
                parameters,
                true /* ignore cache */
            );

            if (repositories && repositories.errorMessage) {
                this.setState({ showLoading: false });
                throw new Error(repositories.errorMessage);
            }
            return repositories!;
        }
    }

    private getClassName(baseClassName: string): string {
        return baseClassName + " github";
    }
}

interface IRepositoryItemProps {
    repository: ExternalGitRepo;
    iconProps: IVssIconProps;
}

class RepositoryItem extends React.Component<IRepositoryItemProps> {
    public render(): JSX.Element {
        return (
            <div className="repository-item">
                <VssDetailsListTitleCell
                    iconProps={this.props.iconProps}
                    onRenderPrimaryText={() => {
                        return (
                            <span className="repository-item-primary-text">
                                <span className="repository-name">{this.props.repository.additionalProperties.repoNameWithOwner}</span>
                                {this._getPropertyTag("isFork", Resources.RepositorySelectorTagFork)}
                                {this._getPropertyTag("isPrivate", Resources.RepositorySelectorTagPrivate)}
                            </span>
                        );
                    }}
                />
            </div>
        );
    }

    private _getPropertyTag(property: string, tagName: string): JSX.Element | null {
        if (this._isPropertyEnabled(property)) {
            return <Pill size={PillSize.compact} className="repo-tag">{tagName}</Pill>;
        }
        return null;
    }

    private _isPropertyEnabled(property: string): boolean {
        return this.props.repository.additionalProperties && this.props.repository.additionalProperties[property];
    }
}
